import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { apiErrorMessage } from "../../lib/inventoryApi";
import { formatDateTime } from "../../lib/format";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";

const KATA_PERAN = { OWNER: "Owner", KEPALA_CABANG: "Kepala Cabang" };
const ALASAN_MIN = 10;

/**
 * K210 / BUSINESS-76 (Owner 12 Okt 2026): "KC region boleh MEMATIKAN saklar DOKU DP/pelunasan (alasan wajib,
 * tercatat); MENYALAKAN kembali hanya Owner."
 *
 * Satu kartu untuk dua layar:
 *   - Owner (Pengaturan): semua cabang, tombol matikan DAN nyalakan kembali per cabang.
 *   - Kepala Cabang (Konfirmasi Pembayaran): cabangnya sendiri, tombol "Matikan DOKU untuk cabang saya" saja —
 *     tidak ada tombol menyalakan (server juga menolaknya).
 * Mati untuk semua cabang (saklar global) tetap di kartu DOKU Pengaturan, khusus Owner.
 */
export default function SaklarDokuCabang({ peran }) {
  const [data, setData] = useState(null);
  const [alasan, setAlasan] = useState({});
  const [sibuk, setSibuk] = useState(null);
  const isOwner = peran === "OWNER";

  const muat = useCallback(async () => {
    try { const r = await api.get("/payments/saklar-doku"); setData(r.data); }
    catch { setData({ galat: true }); }
  }, []);
  useEffect(() => { muat(); }, [muat]);

  if (!data) return null;
  if (data.galat || data.terbaca === false) {
    return (
      <Card className="p-4 text-sm text-muted-foreground" data-testid="saklar-doku-cabang">
        Keadaan DOKU per cabang belum bisa dimuat. Coba muat ulang halaman.
      </Card>
    );
  }
  const globalNyala = data.global?.nyala === true;
  const ubah = async (r, nyala) => {
    setSibuk(r.region_id);
    try {
      await api.post("/payments/saklar-doku", { nyala, alasan: alasan[r.region_id] || "", region_id: r.region_id });
      toast.success(nyala ? `DOKU uang muka & pelunasan cabang ${r.region_nama} dinyalakan kembali.`
                          : `DOKU uang muka & pelunasan cabang ${r.region_nama} DIMATIKAN — customer bayar lewat transfer.`);
      setAlasan((a) => ({ ...a, [r.region_id]: "" }));
      muat();
    } catch (e) {
      toast.error(apiErrorMessage(e, "Gagal mengubah saklar DOKU cabang"));
    } finally {
      setSibuk(null);
    }
  };

  return (
    <Card className="p-4 space-y-3" data-testid="saklar-doku-cabang">
      <div className="text-sm font-medium">DOKU uang muka &amp; pelunasan per cabang</div>
      <p className="text-xs text-muted-foreground">
        {isOwner
          ? "Matikan untuk satu cabang saja bila perlu. Kepala Cabang bisa mematikan cabangnya; menyalakan kembali hanya Owner."
          : "Bila DOKU bermasalah di cabang Anda, matikan di sini — customer bayar lewat transfer yang Anda konfirmasi. Menyalakan kembali hanya Owner."}
        {!globalNyala && " Saat ini DOKU dimatikan Owner untuk semua cabang."}
      </p>
      {(data.region || []).length === 0 && (
        <div className="text-sm text-muted-foreground">Belum ada cabang yang terhubung ke akun Anda.</div>
      )}
      <div className="space-y-3">
        {(data.region || []).map((r) => {
          const nyala = r.nyala === true;
          const teks = (alasan[r.region_id] || "").trim();
          const bolehUbah = isOwner || nyala;
          return (
            <div key={r.region_id} className="rounded-md border border-border p-3 space-y-2"
                 data-testid={`saklar-doku-cabang-${r.region_id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="text-sm font-medium">{r.region_nama}</div>
                <Badge variant={nyala ? "default" : "secondary"} data-testid={`saklar-doku-cabang-status-${r.region_id}`}>
                  {nyala ? "Menyala" : "Dimatikan"}
                </Badge>
              </div>
              {!nyala && (
                <div className="text-xs text-muted-foreground">
                  Dimatikan {KATA_PERAN[r.diubah_oleh_peran] || "Owner"}
                  {r.alasan ? ` — ${r.alasan}` : ""}
                  {r.diubah_at ? ` (${formatDateTime(r.diubah_at)})` : ""}.
                  {!isOwner && " Hanya Owner yang bisa menyalakan kembali."}
                </div>
              )}
              {bolehUbah && (
                <div className="flex flex-wrap items-center gap-2">
                  <Input className="max-w-sm" placeholder={`Alasan (minimal ${ALASAN_MIN} huruf)`}
                         value={alasan[r.region_id] || ""}
                         onChange={(e) => setAlasan((a) => ({ ...a, [r.region_id]: e.target.value }))}
                         data-testid={`saklar-doku-cabang-alasan-${r.region_id}`} />
                  <Button size="sm" variant={nyala ? "destructive" : "default"}
                          disabled={!!sibuk || teks.length < ALASAN_MIN} onClick={() => ubah(r, !nyala)}
                          data-testid={`saklar-doku-cabang-ubah-${r.region_id}`}>
                    {sibuk === r.region_id ? "Menyimpan…"
                      : nyala ? (isOwner ? "Matikan DOKU cabang ini" : "Matikan DOKU untuk cabang saya")
                      : "Nyalakan kembali"}
                  </Button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </Card>
  );
}
